import React, { useEffect, useState } from 'react';
import { ShieldCheckIcon, MapPinIcon, ClockIcon, StarIcon, ZapIcon, RefreshCwIcon } from 'lucide-react';
type Shift = {
  id: number;
  name: string;
  role: string;
  roleColor: string;
  location: string;
  distance: string;
  rating: number;
  reviews: number;
  availability: string;
  hourlyRate: string;
  badges: string[];
  avatar: string;
  isNew: boolean;
};
const SHIFTS: Shift[] = [{
  id: 1,
  name: 'Sarah M.',
  role: 'RN',
  roleColor: 'bg-medical-blue text-white',
  location: 'Toronto, ON',
  distance: '2.1 km',
  rating: 4.9,
  reviews: 142,
  availability: 'Available Now',
  hourlyRate: '$42/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'ICU Certified', 'COVID-19 Trained'],
  avatar: 'SM',
  isNew: true
}, {
  id: 2,
  name: 'James O.',
  role: 'PSW',
  roleColor: 'bg-accent-teal text-white',
  location: 'Mississauga, ON',
  distance: '4.8 km',
  rating: 4.8,
  reviews: 89,
  availability: 'Available in 2h',
  hourlyRate: '$28/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'Dementia Care'],
  avatar: 'JO',
  isNew: false
}, {
  id: 3,
  name: 'Priya K.',
  role: 'RPN',
  roleColor: 'bg-purple-600 text-white',
  location: 'Brampton, ON',
  distance: '6.2 km',
  rating: 5.0,
  reviews: 67,
  availability: 'Available Now',
  hourlyRate: '$36/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'Palliative Care', 'Top Rated'],
  avatar: 'PK',
  isNew: true
}, {
  id: 4,
  name: 'David L.',
  role: 'HCA',
  roleColor: 'bg-orange-500 text-white',
  location: 'Vaughan, ON',
  distance: '8.1 km',
  rating: 4.7,
  reviews: 203,
  availability: 'Available Tomorrow',
  hourlyRate: '$26/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'Long-Term Care'],
  avatar: 'DL',
  isNew: false
}, {
  id: 5,
  name: 'Amara N.',
  role: 'RN',
  roleColor: 'bg-medical-blue text-white',
  location: 'Scarborough, ON',
  distance: '9.4 km',
  rating: 4.9,
  reviews: 118,
  availability: 'Available Now',
  hourlyRate: '$44/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'ER Certified', 'BLS/ACLS'],
  avatar: 'AN',
  isNew: true
}, {
  id: 6,
  name: 'Chen W.',
  role: 'PSW',
  roleColor: 'bg-accent-teal text-white',
  location: 'North York, ON',
  distance: '3.7 km',
  rating: 4.6,
  reviews: 54,
  availability: 'Available in 4h',
  hourlyRate: '$27/hr',
  badges: ['Nurse-Vetted', 'WSIB Covered', 'Bilingual (Mandarin)'],
  avatar: 'CW',
  isNew: false
}];
function ShiftCard({
  shift,
  index
}: {
  shift: Shift;
  index: number;
}) {
  return <div className="bg-white rounded-2xl border border-gray-100 p-5 card-float cursor-pointer" style={{
    animationDelay: `${index * 80}ms`
  }} role="article" aria-label={`${shift.name}, ${shift.role} available in ${shift.location}`} data-id="element-164">
      <div className="flex items-start gap-4" data-id="element-165">
        {/* Avatar */}
        <div className="relative flex-shrink-0" data-id="element-166">
          <div className="w-12 h-12 bg-soft-teal rounded-xl flex items-center justify-center font-bold text-medical-blue text-sm" data-id="element-167">
            {shift.avatar}
          </div>
          {shift.isNew && <span className="absolute -top-1 -right-1 w-3 h-3 bg-accent-teal rounded-full border-2 border-white live-dot" data-id="element-168" />}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0" data-id="element-169">
          <div className="flex items-center justify-between gap-2 mb-1" data-id="element-170">
            <div className="flex items-center gap-2" data-id="element-171">
              <span className="font-bold text-text-dark text-sm" data-id="element-172">
                {shift.name}
              </span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${shift.roleColor}`} data-id="element-173">
                {shift.role}
              </span>
            </div>
            <span className="text-medical-blue font-bold text-sm flex-shrink-0" data-id="element-174">
              {shift.hourlyRate}
            </span>
          </div>

          {/* Location & availability */}
          <div className="flex items-center gap-3 mb-2" data-id="element-175">
            <span className="flex items-center gap-1 text-xs text-text-muted" data-id="element-176">
              <MapPinIcon className="w-3 h-3" data-id="element-177" />
              {shift.location} · {shift.distance}
            </span>
            <span className={`flex items-center gap-1 text-xs font-semibold ${shift.availability === 'Available Now' ? 'text-accent-teal' : 'text-text-muted'}`} data-id="element-178">
              <ClockIcon className="w-3 h-3" data-id="element-179" />
              {shift.availability}
            </span>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-3" data-id="element-180">
            <StarIcon className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" data-id="element-181" />
            <span className="text-xs font-bold text-text-dark" data-id="element-182">
              {shift.rating}
            </span>
            <span className="text-xs text-text-muted" data-id="element-183">
              ({shift.reviews} reviews)
            </span>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5" data-id="element-184">
            {shift.badges.map(badge => <span key={badge} className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${badge === 'Nurse-Vetted' ? 'bg-soft-teal text-accent-teal-dark' : badge === 'WSIB Covered' ? 'bg-blue-50 text-medical-blue border border-blue-100' : badge === 'Top Rated' ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-text-muted'}`} data-id="element-185">
                {badge === 'Nurse-Vetted' && <ShieldCheckIcon className="w-3 h-3" data-id="element-186" />}
                {badge === 'WSIB Covered' && <ShieldCheckIcon className="w-3 h-3" data-id="element-187" />}
                {badge}
              </span>)}
          </div>
        </div>
      </div>

      {/* Action */}
      <div className="mt-4 pt-4 border-t border-gray-50 flex gap-2" data-id="element-188">
        <button className="flex-1 bg-medical-blue hover:bg-medical-blue-dark text-white text-xs font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5" data-id="element-189">
          <ZapIcon className="w-3.5 h-3.5" data-id="element-190" />
          Request Now
        </button>
        <button className="px-4 py-2.5 border-2 border-gray-200 hover:border-medical-blue text-text-muted hover:text-medical-blue text-xs font-bold rounded-xl transition-colors" data-id="element-191">
          View Profile
        </button>
      </div>
    </div>;
}
export function LiveShiftBoard() {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'RN', 'RPN', 'PSW', 'HCA', 'Available Now'];
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);
  const filteredShifts = activeFilter === 'All' ? SHIFTS : activeFilter === 'Available Now' ? SHIFTS.filter(s => s.availability === 'Available Now') : SHIFTS.filter(s => s.role === activeFilter);
  return <section className="w-full bg-gray-50 py-16 lg:py-24" aria-label="Live shift availability board" data-id="element-192">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-193">
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8" data-id="element-194">
          <div data-id="element-195">
            <div className="inline-flex items-center gap-2 bg-soft-teal text-accent-teal-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-3" data-id="element-196">
              <span className="w-2 h-2 bg-accent-teal rounded-full live-dot" data-id="element-197" />
              Live Feed
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight" data-id="element-198">
              Available Workers
            </h2>
            <p className="text-text-muted mt-2" data-id="element-199">
              Real-time availability · Updated every 30 seconds
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-muted" data-id="element-200">
            <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-201" />
            Updated{' '}
            {lastUpdated.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap mb-8" role="group" aria-label="Filter by role" data-id="element-202">
          {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeFilter === filter ? 'bg-medical-blue text-white shadow-sm' : 'bg-white text-text-muted border border-gray-200 hover:border-medical-blue hover:text-medical-blue'}`} aria-pressed={activeFilter === filter} data-id="element-203">
              {filter}
            </button>)}
        </div>

        {/* Shift grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-id="element-204">
          {filteredShifts.map((shift, index) => <ShiftCard key={shift.id} shift={shift} index={index} data-id="element-205" />)}
        </div>

        {/* View all CTA */}
        <div className="mt-10 text-center" data-id="element-206">
          <button className="inline-flex items-center gap-2 bg-white border-2 border-medical-blue text-medical-blue font-bold px-8 py-3.5 rounded-xl hover:bg-soft-teal transition-all" data-id="element-207">
            View All 247 Available Workers
            <ZapIcon className="w-4 h-4" data-id="element-208" />
          </button>
        </div>
      </div>
    </section>;
}